import { useEffect, useState } from "react";
import Card from "../components/Card";
import { Zap, Lightbulb, LayoutGrid } from "lucide-react";
import MapView from "../components/MapView";
import SplashScreen from "../components/SplashScreen";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import MapDash from "../components/MapDash";
import { useTranslation } from "react-i18next";

const Energy = () => {
  const [data, setData] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [diffs, setDiffs] = useState({});

  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const localData = localStorage.getItem("energyFormData");

    if (localData) {
      const parsed = JSON.parse(localData);

      const dataObj = {
        solar_energy_unit: parsed.solar_energy_unit,
        electricity_consumption_unit: parsed.electricity_consumption_unit,
        pv_capacity_mwp: parseFloat(parsed.pv_capacity_mwp),
        latitude: parseFloat(parsed.latitude),
        longitude: parseFloat(parsed.longitude),
        solar_energy_production: parseFloat(parsed.solar_energy_production),
        electricity_consumption: parseFloat(parsed.electricity_consumption),
        solar_coverage_percent: parseFloat(parsed.solar_coverage_percent),
        daily_consumption_per_guest: parseFloat(
          parsed.daily_consumption_per_guest
        ),
        smart_rooms: parseInt(parsed.smart_rooms),
        dimmable_area_percent: parseFloat(parsed.dimmable_area_percent),
      };

      setData(dataObj);
    } else {
      const defaultData = {
        solar_energy_unit: "year",
        electricity_consumption_unit: "year",
        pv_capacity_mwp: 0,
        solar_energy_production: 0,
        electricity_consumption: 0,
        solar_coverage_percent: 0,
        daily_consumption_per_guest: 0,
        smart_rooms: 0,
        dimmable_area_percent: 0,
        monthly_generation_mwh: 0,
      };
      setData(defaultData);
    }

    setTimeout(() => setShowSplash(false), 200);
  }, []);

  useEffect(() => {
    const storedDiffs = JSON.parse(localStorage.getItem("energyFormDataDiff"));
    if (storedDiffs) {
      setDiffs(storedDiffs);
    }
  }, []);

  const handleFilterChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const solarEnergyCards = [
    {
      label: t("solar_capacity"),
      value: `${data?.pv_capacity_mwp} MWp`,
      icon: <Zap className="w-6 h-6 text-yellow-500" />,
      percentage: diffs?.pv_capacity_mwp,
    },
    {
      label: t("solar_coverage"),
      value: `${data?.solar_coverage_percent}%`,
      icon: <Zap className="w-6 h-6 text-green-600" />,
      percentage: diffs?.solar_coverage_percent,
    },
    {
      label: t("solar_production"),
      value: `${data?.solar_energy_production}%`,
      icon: <LayoutGrid className="w-6 h-6 text-green-500" />,
      percentage: diffs?.solar_energy_production,
    },
  ];

  const electricityCards = [
    {
      label: t("electricity_consumption"),
      value: `${data?.electricity_consumption}`,
      icon: <Zap className="w-6 h-6 text-green-500" />,
      percentage: diffs.electricity_consumption,
    },
    {
      label: t("solar_coverage"),
      value: `${data?.solar_coverage_percent}%`,
      icon: <LayoutGrid className="w-6 h-6 text-green-500" />,
      percentage: diffs.solar_coverage_percent,
    },
    {
      label: t("daily_consumption_guest"),
      value: `${data?.daily_consumption_per_guest} kWh/day`,
      icon: <Zap className="w-6 h-6 text-blue-600" />,
      percentage: diffs.daily_consumption_per_guest,
    },
  ];

  const smartRoomCards = [
    {
      label: t("smart_rooms_count"),
      value: data?.smart_rooms,
      icon: <Lightbulb className="w-6 h-6 text-blue-500" />,
      percentage: diffs.smart_rooms,
    },
    {
      label: t("dimmable_area_percent"),
      value: `${data?.dimmable_area_percent}%`,
      icon: <LayoutGrid className="w-6 h-6 text-green-500" />,
      percentage: diffs.dimmable_area_percent,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("energy")} | {t("dashboard")}</title>
      </Helmet>
      {/* الهيكل الرئيسي للصفحة */}
      <div
        className="flex flex-col gap-4 md:gap-3 text-start w-full h-auto md:h-full md:min-h-0 md:overflow-hidden"
      >
        {/* العنوان والفلاتر */}
        <div className="flex flex-col gap-2 text-start">
          <h1 className="mx-auto text-xl sm:text-2xl md:text-3xl font-extrabold p-2.5 bg-white/55 rounded-md backdrop-blur-md w-full flex justify-center">
            {t("energy")}
          </h1>

          {/* الفلاتر */}
          <div className="flex flex-col sm:flex-row gap-2 w-full justify-between items-center">
            <select
              className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
              value={selectedCategory}
              onChange={handleFilterChange}
            >
              <option value="all">{t("all")}</option>
              <option value="solar_energy">{t("solar_energy")}</option>
              <option value="electricity_consunption">{t("electricity_consumption")}</option>
              <option value="smart_rooms">
                {t("smart_rooms")}
              </option>
              <option value="map">{t("map_coordinates")}</option>
            </select>

            <button
              onClick={() => navigate("/EnergyForm")}
              className="bg-green-500 hover:bg-green-600 transition-colors text-white p-2 rounded w-full sm:w-auto text-xs sm:text-sm font-semibold"
            >
              {t("edit_data")}
            </button>
          </div>
        </div>

        {/* المحتوى الرئيسي */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
          {/* الجزء الأول: الكروت */}
          <div className="col-span-1 flex flex-col gap-4 overflow-y-auto pr-2 h-[60vh] md:h-auto md:flex-1 md:min-h-0" dir="ltr">
            {(selectedCategory === "all" ||
              selectedCategory === "solar_energy") && (
              <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3">{t("solar_energy")}</h2>
                <div className="grid grid-cols-1 gap-3">
                  {solarEnergyCards.map((item, i) => (
                    <Card key={`solar-${i}`} {...item} />
                  ))}
                </div>
              </div>
            )}

            {(selectedCategory === "all" ||
              selectedCategory === "electricity_consunption") && (
              <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3">{t("electricity_consumption")}</h2>
                <div className="grid grid-cols-1 gap-3">
                  {electricityCards.map((item, i) => (
                    <Card key={`elec-${i}`} {...item} />
                  ))}
                </div>
              </div>
            )}

            {(selectedCategory === "all" ||
              selectedCategory === "smart_rooms") && (
              <div className="bg-white/30 dark:bg-gray-800/50 p-3 rounded-lg shadow-sm" dir={document.documentElement.dir}>
                <h2 className="text-base sm:text-lg font-bold mb-3">
                  {t("smart_rooms")}
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {smartRoomCards.map((item, i) => (
                    <Card key={`smart-${i}`} {...item} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* الجزء الثاني: الخريطة */}
          {(selectedCategory === "all" || selectedCategory === "map") && (
            <div
              className={`${
                selectedCategory === "map" ? "md:col-span-3" : "md:col-span-2"
              } h-[60vh] md:h-auto md:flex-1 md:min-h-0 rounded-xl leaflet-container !bg-transparent min-h-[300px] shadow-md border border-white/20`}
            >
              <MapView initialData={data} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Energy;


